import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Database,
  Sparkles,
  CheckCircle2,
  Building2,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { FileUploadZone } from "@/components/portal/FileUploadZone";
import { ragApi, CreateBatchResponse } from "@/services/api/rag";

export const Route = createFileRoute("/portal/knowledgebase-file/upload")({
  component: RAGUploadPage,
});

function RAGUploadPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const canCreateKbFile = can("create", "kb-file");
  const isPlatformAdmin = user?.is_admin === true;
  const organisationId = selectedOrganisation?.organisationId || "";
  const orgName = selectedOrganisation?.organisationName || "Organisation";

  const [loading, setLoading] = useState(false);
  const [batchName, setBatchName] = useState(
    `Batch ${new Date().toISOString().split("T")[0]}`,
  );
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<string[]>([]);
  const [createdBatch, setCreatedBatch] = useState<CreateBatchResponse | null>(null);

  const { uploads, addFiles, removeUpload, retryUpload } = useDocumentUpload({
    organisationId,
    token: token || "",
    purpose: "rag",
    onUploadComplete: (documentId) => {
      setUploadedDocumentIds((prev) => [...prev, documentId]);
    },
    onAllComplete: () => {
      toast.success("All files uploaded!");
    },
  });

  if (!canCreateKbFile) {
    return (
      <div className="text-center py-12">
        <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">Access Denied</h3>
        <p className="text-muted-foreground">You don't have permission to upload documents.</p>
      </div>
    );
  }

  if (!organisationId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Upload Documents</h1>
          <p className="mt-2 text-lg text-slate-600">
            Select an organisation from the header to upload documents
          </p>
        </div>
      </div>
    );
  }

  const handleCreateBatch = async () => {
    if (!token) return;

    if (uploadedDocumentIds.length === 0) {
      toast.error("No documents uploaded", {
        description: "Please upload documents first before creating a batch",
      });
      return;
    }

    setLoading(true);
    try {
      // Build document_id -> filename mapping from completed uploads
      const documentNames: Record<string, string> = {};
      for (const upload of uploads) {
        if (upload.documentId && upload.status === "completed") {
          documentNames[upload.documentId] = upload.name;
        }
      }

      const response = await ragApi.createBatch(token, {
        document_ids: uploadedDocumentIds,
        batch_name: batchName || `Batch ${new Date().toISOString().split("T")[0]}`,
        document_names: documentNames,
      });

      setCreatedBatch(response);

      toast.success("Batch created successfully!", {
        description: `Processing ${response.total_documents} documents`,
      });

      setTimeout(() => {
        navigate({ to: "/portal/knowledgebase-file/batches" });
      }, 2000);
    } catch (error) {
      console.error("Failed to create batch:", error);
      toast.error("Failed to create batch", {
        description: "Could not create ingestion batch",
      });
    } finally {
      setLoading(false);
    }
  };

  const hasCompletedUploads = uploadedDocumentIds.length > 0;
  const allUploadsComplete =
    uploads.length > 0 &&
    uploads.every((u) => u.status === "completed" || u.status === "error");

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-slate-900 flex items-center gap-2">
          <Database className="h-8 w-8" />
          Upload Documents
        </h1>
        <p className="mt-2 text-lg text-slate-600">
          Upload documents for AI-powered search and retrieval
        </p>
      </div>

      {/* Org context indicator for platform admins */}
      {isPlatformAdmin && (
        <Alert>
          <Building2 className="h-4 w-4" />
          <AlertTitle>Organisation Context</AlertTitle>
          <AlertDescription>
            Uploading documents to{" "}
            <Badge variant="outline" className="ml-1">
              {orgName}
            </Badge>
            . Switch organisation in the header to upload for a different org.
          </AlertDescription>
        </Alert>
      )}

      {/* Info Alert */}
      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertTitle>Intelligent Document Processing</AlertTitle>
        <AlertDescription>
          Documents are automatically processed using advanced AI to extract
          text, tables, and figures. Content is then indexed for powerful
          semantic search across your knowledge base.
        </AlertDescription>
      </Alert>

      {/* Batch Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Batch Configuration</CardTitle>
          <CardDescription>
            Configure your ingestion batch settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="batch-name">Batch Name (Optional)</Label>
            <Input
              id="batch-name"
              placeholder={`Batch ${new Date().toISOString().split("T")[0]}`}
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              className="mt-1"
              disabled={!!createdBatch}
            />
          </div>
        </CardContent>
      </Card>

      {/* Upload Zone */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload Documents</CardTitle>
          <CardDescription>
            Select files to upload (max 5GB per file). Supported: PDF, Word
            (.doc, .docx), PowerPoint (.ppt, .pptx), Excel (.xlsx, .xls),
            CSV (.csv)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FileUploadZone
            onFilesAdded={addFiles}
            uploads={uploads}
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xlsx,.xls,.csv"
            disabled={!!createdBatch}
            onRemove={removeUpload}
            onRetry={retryUpload}
          />
        </CardContent>
      </Card>

      {/* Create Batch Button */}
      {hasCompletedUploads && !createdBatch && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">
                  Ready to Process Documents
                </h3>
                <p className="text-sm text-muted-foreground">
                  {uploadedDocumentIds.length} document
                  {uploadedDocumentIds.length !== 1 ? "s" : ""} uploaded and
                  ready for ingestion
                </p>
              </div>
              <Button
                onClick={handleCreateBatch}
                disabled={loading || !allUploadsComplete}
                size="lg"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {loading ? "Creating Batch..." : "Create Batch"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batch Created Success */}
      {createdBatch && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900 dark:text-green-100">
            Batch Created Successfully!
          </AlertTitle>
          <AlertDescription className="text-green-800 dark:text-green-200">
            Processing {createdBatch.total_documents} documents with{" "}
            {createdBatch.total_jobs} jobs. Redirecting to batches page...
          </AlertDescription>
        </Alert>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Upload documents using the file selector or drag and drop</li>
            <li>Documents are securely stored in {orgName}'s document store</li>
            <li>Click "Create Batch" to queue documents for processing</li>
            <li>AI extracts text, tables, and figures from each document</li>
            <li>Content is converted into semantic embeddings for intelligent search</li>
            <li>Monitor progress in the Batches page</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
